import Cookies from 'js-cookie';
import EzController from 'controllers/ez-base-controller';
import events from 'lib/events';
import {
  getContentType,
  getWindowWidth,
  triggerCustomEvent,
  isHomepage,
  isSearchPage,
  getCurrentPageParam,
  smoothScroll,
  resetUrlWithParam,
  isSearchByImagePage,
  featureEnabled,
} from 'lib/helpers';
import { displaySearchPopup } from 'lib/partner-popup-helpers';
import SearchStateManager from 'lib/search-state';
import ScrollPositionHelper from 'lib/scroll-position-helpers';
import { sendTrackingEvent, trackCompleteEvent } from 'components/analytics/conversions/tracking';
import {
  POPULAR_SEARCHES_CLICK,
  SEARCH_MANUAL,
  SEARCH_AUTOSUGGEST,
  SEARCH_COMPLETE,
} from 'components/analytics/conversions/constants/actions';

const searchState = new SearchStateManager();
searchState.addPopStateListener(); // only add this once

export default class SearchFormController extends EzController {
  static targets = [
    'form',
    'input',
    'submit',
    'currentLabel',
    'popularSearchesLink',
    'inlineFilters',
    'previousSearches',
    'hiddenInputHolder',
    'previousSearchTerms',
    'autocompleteSuggestions',
  ];

  navigationKeyCodes = [
    'ArrowUp',
    'ArrowLeft',
    'ArrowRight',
    'ArrowDown',
    'Enter',
  ];

  exclusionKeyCodes = [
    'MetaLeft',
    'MetaRight',
    'ControlLeft',
    'ControlRight',
    'AltLeft',
    'AltRight',
    'ShiftLeft',
    'ShiftRight',
  ];

  wrapperToggleClasses = [
    'inline-filters',
    'inline-filters__arrow',
    'inline-filters__current',
  ];

  connect() {
    // init scroll position helper if not already
    if (!window.EezyStickyScrollPositionHelper) window.EezyStickyScrollPositionHelper = ScrollPositionHelper;

    this.autocompleteUrl = this.inputTarget.dataset.autocompleteSource;

    // If not mobile/auto-focus search input & add autocomplete listener
    if (!this.isMobile()) this.autoFocusSearchForm();

    // if we've loaded the scroll pos helper and it doesnt have a scroll value stored, reset the scroll position.
    if (!this.isMobile() && window.EezyScrollPositionHelper && window.EezyScrollPositionHelper.allowFocus()) {
      window.EezyScrollPositionHelper.resetScrollPosition();
    }

    if (isSearchPage()) {
      this.searchFormColorSwatches = document.querySelectorAll('[data-search-form-target="searchedColor"]');
      window.addEventListener('updateEzSearchFormColorSwatch', this.updateSearchSwatch);
    }

    if (this.hasInlineFiltersTarget) this.setupInlineFilters();

    this.checkAndToggleIsTyped();

    // Add is-ready class to search field, so we can apply styles after focused
    // (if we ever remove auto-focus, we need to change/re-think this)
    this.inputTarget.classList.add('is-ready');

    if (isHomepage()) this.clearSearchFiltersHidden();

    this.inputTarget.addEventListener('blur', this.resizeInput);
    this.inputTarget.addEventListener('keyup', this.resizeInput);
    this.inputTarget.addEventListener('keydown', this.resizeInput);
    this.inputTarget.addEventListener('click', this.removeAutoFocus);
    this.inputTarget.addEventListener('blur', this.removeAutoFocus);
    this.inputTarget.addEventListener('keydown', this.removeAutoFocus);

    if (this.inputTarget.value) {
      this.resizeInput();
    }

    const previousSearchData = window.localStorage.getItem('previous_searches');
    if (previousSearchData) {
      this.setPreviousSearches(previousSearchData);
    } else {
      this.previousSearchesTarget.classList.add('is-hidden');
    }

    this.inputTarget.addEventListener('click', this.handleInputClick);

    this.flagLoaded();
  }

  disconnect() {
    document.removeEventListener('click', this.blurInlineFilters);

    this.inputTarget.removeEventListener('blur', this.resizeInput);
    this.inputTarget.removeEventListener('keyup', this.resizeInput);
    this.inputTarget.removeEventListener('keydown', this.resizeInput);
    this.inputTarget.removeEventListener('click', this.removeAutoFocus);
    this.inputTarget.removeEventListener('blur', this.removeAutoFocus);
    this.inputTarget.removeEventListener('keydown', this.removeAutoFocus);

    window.removeEventListener('updateEzSearchFormColorSwatch', this.updateSearchSwatch);

    this.inputTarget.removeEventListener('click', this.handleInputClick);
  }

  focusSearchField = (event) => {
    if (event.target === event.currentTarget) {
      this.inputTarget.focus();
    }
  }

  scrollToSearch = () => {
    if (this.isMobile() && isHomepage()) {
      const scrollPosition = this.inputTarget.getBoundingClientRect();
      const scrollOffset = scrollPosition.top + window.scrollY - 20;
      smoothScroll(scrollOffset);
    }
  }

  updateSearchSwatch = (event) => {
    const swatchColor = event.detail.color;

    this.searchFormColorSwatches.forEach((swatch) => {
      swatch.parentElement.classList.add('ez-search-form__searched-color--active');
      swatch.style.backgroundColor = `#${swatchColor}`;
    });
  }

  resetColorSwatchFilter = () => {
    this.searchFormColorSwatches.forEach((swatch) => {
      swatch.parentElement.classList.remove('ez-search-form__searched-color--active');
      swatch.style.backgroundColor = '';
    });
    triggerCustomEvent('resetColorSwatchFilter');
  }

  resizeInput = () => {
    if (!this.inputTarget.value) {
      this.inputTarget.placeholder = this.savedPlaceholder || this.inputTarget.placeholder;
      this.inputTarget.setAttribute('aria-label', this.savedPlaceholder || this.inputTarget.placeholder);

      this.hiddenInputHolderTarget.textContent = this.inputTarget.placeholder;
    } else {
      this.hiddenInputHolderTarget.textContent = this.inputTarget.value;
    }

    this.inputTarget.style.width = `${this.hiddenInputWidth || 1}px`;

    if (isSearchPage()) {
      this.repositionColorSwatch();
    }
  }

  repositionColorSwatch = () => {
    this.searchFormColorSwatches.forEach((swatch) => {
      swatch.parentElement.style.left = `${this.hiddenInputWidth || 1}px`;
    });
  }

  setupInlineFilters() {
    this.setLandingPageInlineFilterDefaults();

    document.addEventListener('click', this.blurInlineFilters);
  }

  get hiddenInputWidth() {
    return Math.ceil(this.hiddenInputHolderTarget.getBoundingClientRect().width);
  }

  handleInputClick = () => {
    if (this.inputTarget.value.length >= 2) {
      this.queryAutocomplete(this.inputTarget.value);
    }
  }

  clearSearchFiltersHidden() {
    Cookies.remove('ezSearchFiltersHidden');
  }

  autoFocusSearchForm() {
    const searchVal = this.inputTarget.value;
    this.inputTarget.value = '';
    this.inputTarget.focus();
    this.inputTarget.value = searchVal;

    this.element.classList.add('is-auto-focused');
  }

  removeAutoFocus = () => {
    this.element.classList.remove('is-auto-focused');
  }

  setLandingPageInlineFilterDefaults() {
    if (document.body.classList.contains('resource-landing')) {
      searchState.resetStateToDefault();
    } else {
      this.setTogglesFromState();
    }
  }

  setTogglesFromState = () => {
    const activeTogglesFromState = searchState.getRawParamKeys();

    const allFiltersInputs = [
      ...this.element.querySelectorAll('.ez-input--checkbox input'),
    ];

    allFiltersInputs.forEach((checkbox) => {
      const checkboxFilterParam = checkbox.closest('.ez-input--checkbox').getAttribute('data-filter-param');

      if (activeTogglesFromState.includes(checkboxFilterParam)) {
        this.setActiveFilter(checkbox);
      }
    });
  }

  clearPreviousSearches() {
    this.previousSearchesTarget.classList.add('is-hidden');
    window.localStorage.removeItem('previous_searches');
  }

  setPreviousSearches(previousSearchData) {
    const previousSearches = JSON.parse(previousSearchData);

    for (let i = 0; i < previousSearches.length; i += 1) {
      const previousSearch = JSON.parse(previousSearches[i]);
      const previousSearchHtml = `
        <li class="ez-btn ez-btn--light ez-btn--small" data-action="click->search-form#handleSuggestionClick"
            data-content-type="${previousSearch.contentType}">${previousSearch.term}</li>`;

      this.previousSearchTermsTarget.insertAdjacentHTML('beforeend', previousSearchHtml);
    }
  }

  handlePreviousClickBlur(event) {
    event.preventDefault();
  }

  handleFilterClick = (event) => {
    this.wrapperToggleClasses.forEach((wrapperClass) => {
      if (event.target.classList.contains(wrapperClass)) this.toggleInlineFilters();
    });
  }

  setActiveFilter = (inputEl) => {
    const allFilters = [...this.element.querySelectorAll('input')];
    const optionItem = inputEl.closest('.inline-filters__item');

    allFilters.forEach((el) => { el.checked = false; });

    inputEl.checked = true;

    if (getWindowWidth() <= 635) {
      this.currentLabelTarget.innerHTML = '';
      this.currentLabelTarget.appendChild(optionItem.querySelector('svg').cloneNode(true));
    } else {
      this.currentLabelTarget.textContent = optionItem.dataset.selected;
    }

    this.inputTarget.placeholder = optionItem.dataset.searchSelected;
    this.inputTarget.setAttribute('aria-label', optionItem.dataset.searchSelected);

    if (this.hasPopularSearchesLinkTarget) {
      this.popularSearchesLinkTarget.href = optionItem.dataset.popularSearchPath;
      this.popularSearchesLinkTarget.textContent = optionItem.dataset.popularSearchText;
      this.popularSearchesLinkTarget.setAttribute('data-english-label', optionItem.dataset.englishLabel);
    }

    triggerCustomEvent(events.search.update_content_type, {
      contentType: inputEl.value,
    });
  }

  handleFilterOptionClick = (event) => {
    this.setActiveFilter(event.target);

    if (!searchState.omittedFromState(event.target.getAttribute('name'))) {
      searchState.updateParamKey(`${event.target.getAttribute('name')}-${event.target.getAttribute('value')}`);
    }

    this.toggleInlineFilters();

    if (document.body.classList.contains('searches-show')) {
      if (isSearchByImagePage()) {
        resetUrlWithParam('content_type', event.target.value);
      } else if (searchState.omittedFromState(event.target.getAttribute('name'))) {
        triggerCustomEvent(events.search.apply_filters, {
          contentType: event.target.value,
        });
      } else {
        this.submit(event);
      }
    } else {
      this.updateContentType(event.target.value);
      this.autoFocusSearchForm();
    }

    if (featureEnabled('search_by_image')) {
      this.updateSBIPlaceholderAttr(event.target.value);
    }
  }

  updateSBIPlaceholderAttr(contentType) {
    const value = this.element.getAttribute(`data-search-by-image-placeholder-${contentType}`);

    this.element.setAttribute('data-search-by-image-placeholder-value', value);
  }

  updateContentType(newType) {
    this.inputTarget.setAttribute('data-content-type', newType);

    triggerCustomEvent(events.search.update_content_type, {
      contentType: newType,
    });
  }

  trackPopularSearchClick(event) {
    const contentType = this.inputTarget.getAttribute('data-content-type');
    const englishLabel = event.target.getAttribute('data-english-label');

    sendTrackingEvent({
      element: event.target,
      data: {
        action: POPULAR_SEARCHES_CLICK,
        label: `Link-${englishLabel}`,
        content_type: `Content-${contentType}`,
      },
    });
  }

  handlePopularSearchesLinkClick = (event) => {
    this.trackPopularSearchClick(event);
  }

  toggleInlineFilters = () => {
    this.inlineFiltersTarget.classList.toggle('is-open');
  }

  blurInlineFilters = (event) => {
    if (this.inlineFiltersTarget.classList.contains('is-open')
    && !event.target.closest('.ez-search-form .inline-filters')) {
      this.toggleInlineFilters();
    }
  }

  checkAndToggleIsTyped() {
    if (this.searchInputContents) {
      this.element.classList.add('is-typed');
    } else {
      this.element.classList.remove('is-typed');
    }
  }

  handleInputKeyUp = (event) => {
    if (!this.exclusionKeyCodes.includes(event.code) && !this.navigationKeyCodes.includes(event.code)) {
      this.checkAndToggleIsTyped();
    }

    if (isSearchPage()) {
      this.searchFormColorSwatches.forEach((swatch) => {
        if (swatch.style.backgroundColor !== '') {
          setTimeout(() => {
            swatch.parentElement.classList.add('ez-search-form__searched-color--active');
          }, 600);
        }
      });
    }

    if (this.navigationKeyCodes.includes(event.code) && document.querySelector('.ez-search-form__suggestions li')) {
      this.navigateSuggestions(event.code);
    } else if (!this.exclusionKeyCodes.includes(event.code) && this.inputTarget.value.length >= 2) {
      this.queryAutocomplete(this.inputTarget.value);
    } else if (this.autocompleteSuggestionsTarget.innerHTML !== '') {
      this.resetAutocomplete();
    }
  }

  handleInputKeyDown = (event) => {
    if (event.metaKey && event.code === 'Enter') {
      this.submit(event);
    }
  }

  handleSuggestionClick = (event) => {
    this.inputTarget.value = event.currentTarget.textContent;
    this.submit(event);

    this.trackAutoSuggestSearch(event);
  }

  navigateSuggestions = (keyCode) => {
    const autocompleteList = this.autocompleteSuggestionsTarget;
    const activeAutocomplete = autocompleteList.querySelector('li.is-active');
    const recentList = this.previousSearchTermsTarget;
    const activeRecent = recentList.querySelector('li.is-active');

    const setActiveNavSuggestion = (activateEl) => {
      const fullList = document.querySelector('.ez-search-form__suggestions-wrapper');
      const activeEl = fullList.querySelector('li.is-active');

      if (activeEl) {
        activeEl.classList.remove('is-active');
      }
      if (activateEl) {
        activateEl.classList.add('is-active');
        this.inputTarget.value = activateEl.textContent;
      }
    };

    const recentAvailable = (direction) => {
      if (this.previousSearchesTarget.classList.contains('is-hidden')) {
        if (direction === 'navDown') {
          setActiveNavSuggestion(autocompleteList.querySelector('li:first-of-type'));
        } else {
          setActiveNavSuggestion(autocompleteList.querySelector('li:last-of-type'));
        }
      } else {
        setActiveNavSuggestion(recentList.querySelector('li:first-of-type'));
      }
    };

    const navUp = () => {
      if (activeRecent) {
        setActiveNavSuggestion(autocompleteList.querySelector('li:last-of-type'));
      } else if (activeAutocomplete && activeAutocomplete.previousSibling) {
        setActiveNavSuggestion(activeAutocomplete.previousSibling);
      } else {
        recentAvailable('navUp');
      }
    };

    const navDown = () => {
      if (activeAutocomplete && activeAutocomplete.nextSibling) {
        setActiveNavSuggestion(activeAutocomplete.nextSibling);
      } else if (activeAutocomplete && !activeAutocomplete.nextSibling) {
        recentAvailable('navDown');
      } else {
        setActiveNavSuggestion(autocompleteList.querySelector('li:first-of-type'));
      }
    };

    const navRight = () => {
      if (activeRecent) {
        // 750 mobile breakpoint for showing more terms
        if (activeRecent.nextElementSibling && (getWindowWidth() <= 750
          || activeRecent.getBoundingClientRect().top
             === activeRecent.nextElementSibling.getBoundingClientRect().top)) {
          setActiveNavSuggestion(activeRecent.nextElementSibling);
        } else {
          setActiveNavSuggestion(recentList.querySelector('li:first-of-type'));
        }
      }
    };

    const navLeft = () => {
      if (activeRecent) {
        if (activeRecent.previousElementSibling) {
          setActiveNavSuggestion(activeRecent.previousElementSibling);
        } else if (getWindowWidth() <= 750) {
          // 750 mobile breakpoint for showing more terms
          setActiveNavSuggestion(recentList.querySelector('li:last-of-type'));
        } else {
          // get last element in row
          const topLine = activeRecent.getBoundingClientRect().top;
          let previousElement;
          recentList.querySelectorAll('li').forEach((element) => {
            if (element.getBoundingClientRect().top !== topLine) {
              return false;
            }
            previousElement = element;
            return true;
          });
          setActiveNavSuggestion(previousElement);
        }
      }
    };

    if (keyCode === 'ArrowUp') {
      navUp();
    } else if (keyCode === 'ArrowDown') {
      navDown();
    } else if (keyCode === 'ArrowRight') {
      navRight();
    } else if (keyCode === 'ArrowLeft') {
      navLeft();
    } else if (keyCode === 'Enter') {
      this.resetAutocomplete();
    }
  }

  resetAutocomplete = () => {
    this.data.set('lastTerm', '');
    this.autocompleteSuggestionsTarget.innerHTML = '';
    this.formTarget.classList.remove('is-suggesting');
  }

  renderAutocomplete(res, req) {
    this.autocompleteSuggestionsTarget.innerHTML = '';
    this.formTarget.classList.add('is-suggesting');

    if (!res.length > 0) {
      this.resetAutocomplete();
      return;
    }

    Object.keys(res).forEach((i) => {
      const node = document.createElement('LI');
      const formattedReq = req.toLowerCase();

      if (res[i].indexOf(formattedReq) >= 0) {
        node.innerHTML = res[i].replace(formattedReq, `<strong>${formattedReq}</strong>`);
      } else {
        node.innerHTML = res[i];
      }

      node.dataset.action = 'mousedown->search-form#handleSuggestionClick';
      this.autocompleteSuggestionsTarget.appendChild(node);
    });
  }

  queryAutocomplete = (req) => {
    if (req === this.data.get('lastTerm')) return;
    this.data.set('lastTerm', this.inputTarget.value);

    const xhr = new XMLHttpRequest();
    const contentType = this.inputTarget.getAttribute('data-content-type');
    xhr.open('GET', `${this.autocompleteUrl}?term=${encodeURI(req)}&content_type=${contentType}`, true);
    xhr.setRequestHeader('Content-Type', 'text/plain');

    xhr.onload = () => {
      try {
        this.renderAutocomplete(JSON.parse(xhr.response), req);
      } catch (error) { /* Do Nothing */ }
    };

    xhr.send();
  }

  isMobile() {
    return document.body.classList.contains('is-mobile');
  }

  resetPagination() {
    searchState.updateStateValue('page', 1);
  }

  submitViaButton(event) {
    event.preventDefault();
    this.submit(event);
  }

  submit(event) {
    if (this.element.hasAttribute('data-disable-form-submit')) return;

    this.resetPagination();

    const searchedEventDetail = {
      term: this.inputTarget.value,
      searchRoute: this.inputTarget.getAttribute('data-search-route'),
      useSearchState: true,
      contentType: this.inputTarget.getAttribute('data-content-type'),
    };

    if (event.metaKey && event.code && event.code === 'Enter') {
      searchedEventDetail.searchInNewTab = true;
    }

    Cookies.set('ezSearchType', 'internal');

    triggerCustomEvent('Searched', searchedEventDetail);

    this.savePreviousSearch(searchedEventDetail);

    this.trackSearchComplete();

    displaySearchPopup({ searchTerm: this.inputTarget.value });
  }

  savePreviousSearch(searchedEventDetail) {
    let previousSearches = JSON.parse(window.localStorage.getItem('previous_searches'));
    if (!previousSearches) {
      previousSearches = [];
    }
    for (let i = 0; i < previousSearches.length; i += 1) {
      const previousSearch = JSON.parse(previousSearches[i]);
      if (previousSearch.term === searchedEventDetail.term) {
        previousSearches.splice(i, 1);
        break;
      }
    }
    if (previousSearches.unshift(JSON.stringify(searchedEventDetail)) > 6) {
      previousSearches.pop();
    }
    window.localStorage.setItem('previous_searches', JSON.stringify(previousSearches));
  }

  trackManualSearchClick(event) {
    if (this.element.hasAttribute('data-disable-form-submit')) return;

    sendTrackingEvent({
      element: event.target,
      data: {
        action: SEARCH_MANUAL,
        label: `Terms-${this.inputTarget.value}`,
        content_type: `Content-${getContentType()}`,
      },
    });
  }

  trackAutoSuggestSearch(event) {
    sendTrackingEvent({
      element: event.target,
      data: {
        action: SEARCH_AUTOSUGGEST,
        label: `Terms-${this.inputTarget.value}`,
        content_type: `Content-${getContentType()}`,
      },
    });
  }

  trackSearchComplete() {
    trackCompleteEvent({
      action: SEARCH_COMPLETE,
      label: `Terms-${this.inputTarget.value}`,
      valueElementSelector: '.search-results__meta__results-info',
      valueAttribute: 'search-results-count',
      dimensions: {
        content_type: `Content-${getContentType()}`,
      },
    });
  }

  get resultsCount() {
    const searchInfo = document.querySelector('[data-search-results-count]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-results-count');
    }

    return null;
  }

  get pageNumber() {
    const pageParam = getCurrentPageParam();

    if (pageParam) {
      return pageParam;
    }

    return '1';
  }

  get searchTerm() {
    return this.element.getAttribute('data-search-term').replace(/ /g, '-');
  }

  get searchType() {
    return this.element.getAttribute('data-search-type');
  }

  get searchContentType() {
    return this.inputTarget.getAttribute('data-content-type');
  }

  get searchInputContents() {
    return this.inputTarget.value;
  }
}
